import {
    availableLenses,
    availableWindowRoles,
    formatHorizonLabel,
    formatMetricValue,
    LENS_LABELS,
    LENS_ORDER,
    metricOptionsForLens,
    metricValue,
    WINDOW_ROLE_LABELS,
    WINDOW_ROLE_ORDER,
    type IdeaLabLens,
    type IdeaLabRunCell,
    type IdeaLabRunReport,
    type IdeaLabRunSummary,
    type IdeaLabRunVariant,
    type IdeaLabWindowRole,
    type ReportMetric
} from './ideaLabUtils';

/**
 * Report view model shared by the study Results tab and the run page: which slice of a run report
 * is on screen (lens, window, horizon, selected variant), the scoreboard built from it, and the URL
 * encoding that makes every slice linkable.
 */

export type IdeaLabMarket = IdeaLabRunCell['entityType'];
export const marketGroupLabel = (entityType: IdeaLabMarket, lens: IdeaLabLens): string => {
    if (lens === 'futures') return 'ETF signals → native futures';
    if (entityType === 'All') return `Previously pooled ${LENS_LABELS[lens].toLowerCase()}`;
    if (entityType === 'Cryptocurrency') return 'Cryptocurrency';
    if (entityType === 'AssetIndex') return lens === 'option' ? 'Index options' : 'Index movement (research only)';
    return lens === 'option' ? 'Stock/ETF options' : 'Stocks/ETFs';
};
export const availableMarketGroups = (report: Pick<IdeaLabRunReport, 'cells'>) => {
    const groups = new Map<string, { entityType: IdeaLabMarket; lens: IdeaLabLens; label: string }>();
    for (const cell of report.cells)
        groups.set(`${cell.entityType}:${cell.lens}`, {
            entityType: cell.entityType,
            lens: cell.lens,
            label: marketGroupLabel(cell.entityType, cell.lens)
        });
    const order: IdeaLabMarket[] = ['Asset', 'AssetIndex', 'Cryptocurrency', 'All'];
    return [...groups.values()].sort(
        (a, b) => order.indexOf(a.entityType) - order.indexOf(b.entityType) || LENS_ORDER.indexOf(a.lens) - LENS_ORDER.indexOf(b.lens)
    );
};

/** Filter result details and option diagnostics; entry coverage remains a run-wide operational summary. */
export const reportForMarket = (report: IdeaLabRunReport, market: IdeaLabMarket): IdeaLabRunReport => {
    const matches = (row: { entityType: string }) => market === 'All' || row.entityType === market;
    return {
        ...report,
        walkForwardYears: report.walkForwardYears.filter(matches),
        cells: report.cells.filter((cell) => cell.entityType === market),
        entityCells: report.entityCells.filter(matches),
        diffs: report.diffs.filter((cell) => cell.entityType === market),
        optionReasons: report.optionReasons.filter(matches),
        optionLifecycles: report.optionLifecycles.filter(matches),
        optionSelections: report.optionSelections.filter(matches),
        outcomeCauses: report.outcomeCauses.filter(matches),
        meanReturnComparisons: report.meanReturnComparisons.filter(matches),
        entryCoverage: report.entryCoverage
    };
};

export type ReportViewMode = 'scoreboard' | 'horizons';

export interface ReportView {
    entityType: IdeaLabMarket;
    lens: IdeaLabLens;
    windowRole: IdeaLabWindowRole;
    /** Null only when the report has no horizon for the lens. */
    horizonSeconds: number | null;
    variantId: number | null;
    mode: ReportViewMode;
    /** Metric of the by-horizon matrix; the scoreboard shows every metric side by side. */
    metric: ReportMetric;
}

/** Unvalidated view coordinates, e.g. from the URL or local component state. */
export interface ReportViewRequest {
    entityType?: string | null;
    lens?: string | null;
    windowRole?: string | null;
    horizonSeconds?: number | null;
    variantId?: number | null;
    mode?: string | null;
    metric?: string | null;
}

type ReportViewSource = Pick<IdeaLabRunReport, 'cells' | 'horizons' | 'variants'>;

/** Sorted fixed horizons the report measured for one lens. */
export const horizonsForLens = (report: Pick<IdeaLabRunReport, 'horizons'>, lens: IdeaLabLens): number[] =>
    [...new Set(report.horizons.filter((horizon) => horizon.lens === lens).map((horizon) => horizon.horizonSeconds))].sort(
        (left, right) => left - right
    );

const isLens = (value: string | null | undefined): value is IdeaLabLens => LENS_ORDER.some((lens) => lens === value);

const isWindowRole = (value: string | null | undefined): value is IdeaLabWindowRole => WINDOW_ROLE_ORDER.some((role) => role === value);

/**
 * Resolves a requested view against what the report contains: each coordinate the report lacks
 * falls back to a default (first lens, training window, shortest horizon, study control).
 */
export const resolveReportView = (report: ReportViewSource, requested: ReportViewRequest): ReportView => {
    const lenses = availableLenses(report);
    const groups = availableMarketGroups(report);
    const group =
        groups.find((candidate) => candidate.entityType === requested.entityType && candidate.lens === requested.lens) ??
        groups.find((candidate) => candidate.entityType === requested.entityType) ??
        groups.find((candidate) => candidate.lens === requested.lens) ??
        groups[0];
    const entityType = group?.entityType ?? 'All';
    const lens = group?.lens ?? (isLens(requested.lens) && lenses.includes(requested.lens) ? requested.lens : (lenses[0] ?? 'underlying'));
    const windowRoles = availableWindowRoles(report);
    const windowRole =
        isWindowRole(requested.windowRole) && windowRoles.includes(requested.windowRole)
            ? requested.windowRole
            : windowRoles.includes('training')
              ? 'training'
              : (windowRoles[0] ?? 'wholeRange');
    const horizons = horizonsForLens(report, lens);
    const horizonSeconds =
        requested.horizonSeconds != null && horizons.includes(requested.horizonSeconds) ? requested.horizonSeconds : (horizons[0] ?? null);
    const requestedVariant = report.variants.find((variant) => variant.variantId === requested.variantId);
    const defaultVariant = report.variants.find((variant) => variant.role === 'control') ?? report.variants[0];
    const metric = metricOptionsForLens(lens).find((option) => option.value === requested.metric)?.value ?? 'meanReturnPercent';

    return {
        entityType,
        lens,
        windowRole,
        horizonSeconds,
        variantId: (requestedVariant ?? defaultVariant)?.variantId ?? null,
        mode: requested.mode === 'horizons' ? 'horizons' : 'scoreboard',
        metric
    };
};

/** The slice in words, e.g. "Underlying · Training · 7 days". */
export const describeReportSlice = (view: Pick<ReportView, 'entityType' | 'lens' | 'windowRole' | 'horizonSeconds'>): string =>
    [
        marketGroupLabel(view.entityType, view.lens),
        WINDOW_ROLE_LABELS[view.windowRole],
        view.horizonSeconds === null ? null : formatHorizonLabel(view.horizonSeconds)
    ]
        .filter((part): part is string => part !== null)
        .join(' · ');

/** Search-param names of the run report view. */
const VIEW_PARAMS = {
    entityType: 'market',
    lens: 'lens',
    windowRole: 'window',
    horizonSeconds: 'horizon',
    variantId: 'variant',
    mode: 'view',
    metric: 'metric'
} as const;

const parsePositiveIntParam = (value: string | null): number | null => {
    if (value === null || !/^\d+$/.test(value)) return null;

    const parsed = Number(value);

    return parsed > 0 ? parsed : null;
};

export const parseReportViewParams = (params: URLSearchParams): ReportViewRequest => ({
    entityType: params.get(VIEW_PARAMS.entityType),
    lens: params.get(VIEW_PARAMS.lens),
    windowRole: params.get(VIEW_PARAMS.windowRole),
    horizonSeconds: parsePositiveIntParam(params.get(VIEW_PARAMS.horizonSeconds)),
    variantId: parsePositiveIntParam(params.get(VIEW_PARAMS.variantId)),
    mode: params.get(VIEW_PARAMS.mode),
    metric: params.get(VIEW_PARAMS.metric)
});

/** Writes a resolved view into a copy of `params`, leaving unrelated params untouched. */
export const writeReportViewParams = (params: URLSearchParams, view: ReportView): URLSearchParams => {
    const next = new URLSearchParams(params);

    next.set(VIEW_PARAMS.entityType, view.entityType);
    next.set(VIEW_PARAMS.lens, view.lens);
    next.set(VIEW_PARAMS.windowRole, view.windowRole);
    if (view.horizonSeconds === null) next.delete(VIEW_PARAMS.horizonSeconds);
    else next.set(VIEW_PARAMS.horizonSeconds, String(view.horizonSeconds));
    if (view.variantId === null) next.delete(VIEW_PARAMS.variantId);
    else next.set(VIEW_PARAMS.variantId, String(view.variantId));
    if (view.mode === 'scoreboard') next.delete(VIEW_PARAMS.mode);
    else next.set(VIEW_PARAMS.mode, view.mode);
    if (view.mode === 'horizons') next.set(VIEW_PARAMS.metric, view.metric);
    else next.delete(VIEW_PARAMS.metric);

    return next;
};

/** Run page URL opened on one report slice, e.g. from a study scoreboard row. */
export const buildRunReportPath = (runId: number, view?: ReportView): string => {
    if (!view) return `/user/ideaLab/run/${runId}`;

    return `/user/ideaLab/run/${runId}?${writeReportViewParams(new URLSearchParams(), view).toString()}`;
};

/** Run page URL with one variant selected and every other coordinate at its default. */
export const buildRunVariantPath = (runId: number, variantId: number): string =>
    `/user/ideaLab/run/${runId}?${new URLSearchParams({ [VIEW_PARAMS.variantId]: String(variantId) }).toString()}`;

export type ScoreboardColumnKey =
    | 'supportCount'
    | 'tradabilityRate'
    | 'winRate'
    | 'meanReturnPercent'
    | 'medianReturnPercent'
    | 'meanVsControl'
    | 'benchmarkEdgeMeanPercent'
    | 'randomEdgeMeanPercent'
    | 'optionMeanNetProfit'
    | 'futuresMeanNetCash'
    | 'futuresMeanReturnOnAllocatedCapitalPercent';

export interface ScoreboardColumn {
    key: ScoreboardColumnKey;
    label: string;
    description: string;
    /** Whether the highest value in the column is marked as the best. */
    highlightBest: boolean;
    /**
     * The sign carries meaning (a return or an edge): values are colored by sign, and the best is
     * marked only when it is positive, so the least-bad loss never reads as a win.
     */
    signed: boolean;
}

const SCOREBOARD_COLUMNS: Record<ScoreboardColumnKey, ScoreboardColumn> = {
    futuresMeanNetCash: {
        key: 'futuresMeanNetCash',
        label: 'Native net cash (USD)',
        description: 'Mean exact native cash after fees.',
        highlightBest: true,
        signed: true
    },
    futuresMeanReturnOnAllocatedCapitalPercent: {
        key: 'futuresMeanReturnOnAllocatedCapitalPercent',
        label: 'Return on reserve',
        description: 'Mean net cash divided by opening research reserve.',
        highlightBest: true,
        signed: true
    },
    supportCount: {
        key: 'supportCount',
        label: 'Entries',
        description: 'Priced entries in this slice (immature and missing outcomes are not counted)',
        highlightBest: false,
        signed: false
    },
    tradabilityRate: {
        key: 'tradabilityRate',
        label: 'Tradable',
        description: 'Share of entries where a contract matched the policy and could be entered',
        highlightBest: true,
        signed: false
    },
    winRate: {
        key: 'winRate',
        label: 'Win rate',
        description: 'Share of priced entries with a positive return',
        highlightBest: true,
        signed: false
    },
    meanReturnPercent: {
        key: 'meanReturnPercent',
        label: 'Mean',
        description: 'Mean fixed-horizon return of priced entries (options: net dollars over the one-contract opening premium)',
        highlightBest: true,
        signed: true
    },
    medianReturnPercent: {
        key: 'medianReturnPercent',
        label: 'Median',
        description: 'Median fixed-horizon return of priced entries',
        highlightBest: true,
        signed: true
    },
    meanVsControl: {
        key: 'meanVsControl',
        label: 'vs control',
        description: "Mean return minus the control's mean return in this same run, in percentage points",
        highlightBest: true,
        signed: true
    },
    benchmarkEdgeMeanPercent: {
        key: 'benchmarkEdgeMeanPercent',
        label: 'Edge vs SPY',
        description: 'Mean of return minus the same-window SPY return',
        highlightBest: true,
        signed: true
    },
    randomEdgeMeanPercent: {
        key: 'randomEdgeMeanPercent',
        label: 'Edge vs random',
        description: 'Mean of return minus the platform random-entry reference; the small count shows how many entries had a reference',
        highlightBest: true,
        signed: true
    },
    optionMeanNetProfit: {
        key: 'optionMeanNetProfit',
        label: 'Net P&L',
        description: 'Mean net dollar P&L of one contract after fees',
        highlightBest: true,
        signed: true
    }
};

const LENS_SCOREBOARD_COLUMNS: Record<IdeaLabLens, ScoreboardColumnKey[]> = {
    underlying: [
        'supportCount',
        'winRate',
        'meanReturnPercent',
        'medianReturnPercent',
        'meanVsControl',
        'benchmarkEdgeMeanPercent',
        'randomEdgeMeanPercent'
    ],
    futures: [
        'supportCount',
        'winRate',
        'meanReturnPercent',
        'medianReturnPercent',
        'meanVsControl',
        'futuresMeanNetCash',
        'futuresMeanReturnOnAllocatedCapitalPercent'
    ],
    option: [
        'supportCount',
        'tradabilityRate',
        'winRate',
        'meanReturnPercent',
        'medianReturnPercent',
        'meanVsControl',
        'optionMeanNetProfit',
        'randomEdgeMeanPercent'
    ]
};

export interface ScoreboardRow {
    variant: IdeaLabRunVariant;
    /** The pooled cell of this slice; null when the variant has no cell in it. */
    cell: IdeaLabRunCell | null;
    values: Record<ScoreboardColumnKey, number | null>;
}

export interface Scoreboard {
    columns: ScoreboardColumn[];
    rows: ScoreboardRow[];
    control: IdeaLabRunVariant | null;
    /** Highest value per highlighted column, present only when at least two rows compete for it and they differ. */
    best: Partial<Record<ScoreboardColumnKey, number>>;
}

const cellMetric = (cell: IdeaLabRunCell | null, metric: ReportMetric): number | null => (cell === null ? null : metricValue(cell, metric));

/**
 * Every variant of one lens, window, and horizon with its metrics side by side, the difference from
 * the run's control, and the best value per column. Rows keep the report's variant order.
 */
export const buildScoreboard = (
    report: ReportViewSource,
    view: Pick<ReportView, 'entityType' | 'lens' | 'windowRole' | 'horizonSeconds'>
): Scoreboard => {
    const cellsByVariant = new Map<number, IdeaLabRunCell>();

    for (const cell of report.cells) {
        if (
            cell.entityType === view.entityType &&
            cell.lens === view.lens &&
            cell.windowRole === view.windowRole &&
            cell.horizonSeconds === view.horizonSeconds
        ) {
            cellsByVariant.set(cell.variantId, cell);
        }
    }

    const control = report.variants.find((variant) => variant.role === 'control') ?? null;
    const controlMean = control === null ? null : cellMetric(cellsByVariant.get(control.variantId) ?? null, 'meanReturnPercent');
    const columns = LENS_SCOREBOARD_COLUMNS[view.lens].map((key) => SCOREBOARD_COLUMNS[key]);

    const rows = report.variants.map((variant): ScoreboardRow => {
        const cell = cellsByVariant.get(variant.variantId) ?? null;
        const mean = cellMetric(cell, 'meanReturnPercent');

        return {
            variant,
            cell,
            values: {
                supportCount: cellMetric(cell, 'supportCount'),
                tradabilityRate: cellMetric(cell, 'tradabilityRate'),
                winRate: cellMetric(cell, 'winRate'),
                meanReturnPercent: mean,
                medianReturnPercent: cellMetric(cell, 'medianReturnPercent'),
                meanVsControl:
                    variant.variantId === control?.variantId || mean === null || controlMean === null ? null : mean - controlMean,
                benchmarkEdgeMeanPercent: cellMetric(cell, 'benchmarkEdgeMeanPercent'),
                randomEdgeMeanPercent: cellMetric(cell, 'randomEdgeMeanPercent'),
                futuresMeanNetCash: cellMetric(cell, 'futuresMeanNetCash'),
                futuresMeanReturnOnAllocatedCapitalPercent: cellMetric(cell, 'futuresMeanReturnOnAllocatedCapitalPercent'),
                optionMeanNetProfit: cellMetric(cell, 'optionMeanNetProfit')
            }
        };
    });

    const best: Partial<Record<ScoreboardColumnKey, number>> = {};

    for (const column of columns) {
        if (!column.highlightBest) continue;

        const values = rows.flatMap((row) => {
            const value = row.values[column.key];

            return value === null || !Number.isFinite(value) ? [] : [value];
        });

        const highest = values.length >= 2 ? Math.max(...values) : null;
        // A column where every row ties has no best.
        const isDistinct = highest !== null && values.some((value) => value < highest);

        if (highest !== null && isDistinct && (!column.signed || highest > 0)) best[column.key] = highest;
    }

    return { columns, rows, control, best };
};

export interface ScoreboardSort {
    key: ScoreboardColumnKey;
    direction: 'asc' | 'desc';
}

/** Sorted copy of the rows; missing values always sort last. */
export const sortScoreboardRows = (rows: readonly ScoreboardRow[], sort: ScoreboardSort | null): ScoreboardRow[] => {
    if (sort === null) return [...rows];

    const sign = sort.direction === 'asc' ? 1 : -1;

    return [...rows].sort((left, right) => {
        const leftValue = left.values[sort.key];
        const rightValue = right.values[sort.key];

        if (leftValue === null) return rightValue === null ? 0 : 1;
        if (rightValue === null) return -1;

        return (leftValue - rightValue) * sign;
    });
};

/** Signed difference in percentage points, e.g. "+0.213 pp". */
export const formatPercentagePoints = (value: number | null): string => {
    if (value === null || !Number.isFinite(value)) return '—';

    return `${value > 0 ? '+' : ''}${value.toFixed(3)} pp`;
};

/**
 * Lenses a variant can be promoted on: the underlying whenever the run measured it, options only
 * when the run froze an explicit call/put choice (older unresolved runs cannot execute a contract).
 */
export const promotableLenses = (
    run: Pick<IdeaLabRunSummary, 'includeUnderlying' | 'optionLens'> & Partial<Pick<IdeaLabRunSummary, 'futures'>>
): IdeaLabLens[] => [
    ...(run.includeUnderlying ? (['underlying'] as const) : []),
    ...(run.optionLens !== null && run.optionLens.fixedOptionType !== null ? (['option'] as const) : []),
    ...(run.futures?.length ? (['futures'] as const) : [])
];

/** Why a run's variants cannot be promoted, or null when they can. */
export const describePromotionBlocker = (run: Pick<IdeaLabRunSummary, 'status' | 'includeUnderlying' | 'optionLens'>): string | null => {
    if (run.status !== 'completed') return 'Only a completed run can be promoted.';
    if (promotableLenses(run).length === 0) {
        return 'This run measured options without a recorded call or put choice, so it cannot be promoted. Use "Run again with changes" and choose Call or Put.';
    }

    return null;
};

const escapeMarkdownCell = (text: string): string => text.replaceAll('|', '\\|').replaceAll('\n', ' ');

/**
 * A scoreboard slice as Markdown for pasting into notes, chats, or an AI agent: the run, its frozen
 * facts, the slice, one row per variant (best values in bold), a link back, and the exposure note.
 */
export const buildScoreboardMarkdown = (params: {
    title: string;
    facts: readonly string[];
    sliceLabel: string;
    scoreboard: Scoreboard;
    url: string;
    exposureNote: string;
}): string => {
    const { scoreboard } = params;
    const header = ['Variant', ...scoreboard.columns.map((column) => column.label)];
    const formatCell = (row: ScoreboardRow, column: ScoreboardColumn): string => {
        if (row.cell === null) return '—';
        if (column.key === 'meanVsControl' && row.variant.role === 'control') return 'baseline';

        const value = row.values[column.key];
        const text =
            column.key === 'meanVsControl' ? formatPercentagePoints(value) : formatMetricValue(column.key satisfies ReportMetric, value);
        const coverage = column.key === 'randomEdgeMeanPercent' ? ` (${row.cell.randomEdgeCoveredCount}/${row.cell.supportCount})` : '';

        return value !== null && scoreboard.best[column.key] === value ? `**${text}**${coverage}` : `${text}${coverage}`;
    };
    const rows = scoreboard.rows.map((row) => [
        escapeMarkdownCell(
            `#${row.variant.variantId} ${row.variant.variantKey}${row.variant.role === 'control' ? ' (control)' : ''}: ${row.variant.label}`
        ),
        ...scoreboard.columns.map((column) => formatCell(row, column))
    ]);

    return [
        `## ${escapeMarkdownCell(params.title)}`,
        '',
        params.facts.join(' · '),
        `Slice: ${params.sliceLabel}`,
        `Link: ${params.url}`,
        '',
        `| ${header.join(' | ')} |`,
        `| --- |${scoreboard.columns.map(() => ' ---: |').join('')}`,
        ...rows.map((cells) => `| ${cells.join(' | ')} |`),
        '',
        'Bold marks the best value per column (returns and edges only when positive). All figures are descriptive; standard errors and confidence limits are withheld.',
        `Exposure: ${params.exposureNote}`
    ].join('\n');
};
